import { FC, useContext, useEffect, useState } from 'react'

import { useSelector } from 'react-redux'

import {
  OrderStatus,
  ValidateOrdersMutationVariables,
} from '__generated__/atlas-types'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { isLiteRetailer as isLiteRetailerSelector } from 'store/currentUser/selectors'

import { TEMPLATE_SELECTION } from '../../ImportOrders'
import { ImportOrdersContext } from '../../ImportOrders.context'
import { ImportOrdersVerticalHorizontalContext } from '../../ImportOrdersVerticalHorizontal.context'
import {
  useUploadFileToS3,
  useValidateOrders,
} from './UploadFileVerticalHorizontal.hooks'
import {
  ButtonsWrapper,
  Container,
  OptionButton,
  ProgressBar,
} from './UploadFileVerticalHorizontal.styles'
import {
  ProgressData,
  UploadFileProgressBarProps,
  UploadToS3Response,
  ValidateOrderSettings,
} from './UploadFileVerticalHorizontal.types'

const UploadFileProgressBar: FC<UploadFileProgressBarProps> = ({
  nextStep,
}) => {
  const [progressData, setProgressData] = useState<ProgressData>({
    progress: 0,
    label: '',
    error: false,
  })

  const {
    brand,
    retailer,
    door,
    orderType,
    selectedFiles,
    templateId,
    warehouse,
    setNextDisabled,
    setValidatedOrders,
    setShowTitleBar,
    setCloseOrderImportModal,
    setCurrentStep,
  } = useContext(ImportOrdersContext)

  const {
    priceType,
    linesheet,
    orderStatus,
    billingAddress,
    shippingAddress,
    paymentMethod,
    shippingMethod,
  } = useContext(ImportOrdersVerticalHorizontalContext)

  const [uploadToS3Response, setUploadToS3Response] =
    useState<UploadToS3Response | null>(null)
  const uploadFileToS3 = useUploadFileToS3()
  const validateOrders = useValidateOrders()

  const isLiteRetailer = useSelector(isLiteRetailerSelector) as boolean

  const uploadFile = async (file: File): Promise<void> => {
    try {
      const response = await uploadFileToS3({ file: file })
      if (response.errors !== undefined) {
        setProgressData({
          progress: 100,
          label: response.errors[0].message,
          error: true,
        })
      } else if (
        response?.data?.uploadFileToS3?.filename === '' &&
        response?.data?.uploadFileToS3?.id === '' &&
        response?.data?.uploadFileToS3?.mimeType === '' &&
        response?.data?.uploadFileToS3?.url === ''
      ) {
        setProgressData({
          progress: 100,
          label: 'There was an error uploading file to S3',
          error: true,
        })
      } else {
        setProgressData({
          progress: 66,
          label: 'Validating File Contents',
          error: false,
        })

        setUploadToS3Response(response.data.uploadFileToS3)
      }
    } catch (e) {
      setProgressData({
        progress: 100,
        label: String(e),
        error: true,
      })
    }
  }

  const validateOrder = async (
    validateOrdersVars: ValidateOrdersMutationVariables,
  ): Promise<void> => {
    try {
      const orders = await validateOrders(validateOrdersVars)
      if (orders.errors !== undefined) {
        setProgressData({
          progress: 100,
          label: orders.errors[0].message,
          error: true,
        })
      } else {
        setProgressData({
          progress: 100,
          label:
            'Upload completed and file validated, preparing order previews',
          error: false,
        })
        const validateOrdersArgument = {
          ...orders.data.validateOrders,
          validatedOrdersUrl: validateOrdersVars?.url,
        }
        setValidatedOrders(validateOrdersArgument)
        setNextDisabled(false)
        setTimeout(() => nextStep(), 1500)
      }
    } catch (e) {
      setProgressData({
        progress: 100,
        label: String(e),
        error: true,
      })
    }
  }

  useEffect(() => {
    setShowTitleBar(false)
    setProgressData({
      progress: 33,
      label: 'Uploading File',
      error: false,
    })
    setNextDisabled(true)
    uploadFile(selectedFiles[0])

    return () => setShowTitleBar(true)
  }, [])

  useEffect(() => {
    if (uploadToS3Response) {
      // Connected account ids are formatted as BrandAccount or RetailerAccount
      // We need to send Brand or Retailer
      // Brand id is already encoded as Brand in SecondStepVerticalHorizontal
      const brandId = brand?.value || ''
      const retailerId =
        toGlobalId('Retailer', fromGlobalId(retailer?.value).id) || ''
      const templId = toGlobalId('OrdersTemplate', fromGlobalId(templateId).id)

      const validateOrderSettings: ValidateOrderSettings = {
        caseSensitiveColorNames: false,
        caseSensitiveSizeNames: false,
        caseSensitiveProductNumbers: false,
      }

      const validateOrdersVars: any = {
        url: uploadToS3Response?.url,
        templateId: templId,
        settings: validateOrderSettings,
        orderSettings: {
          orderStatus: (orderStatus?.value as OrderStatus) || '',
          collection: {
            id: linesheet?.value || '',
            name: linesheet?.label || '',
          },
          ...(isLiteRetailer ? { brandId } : { customerId: retailerId }),
          priceTypeId: priceType?.value || '',
          orderTypeId: orderType?.value,
          warehouseId: warehouse?.value,
        },
      }
      validateOrdersVars.orderSettings.retailerLocationId = ''

      if (billingAddress) {
        const { value } = billingAddress

        // Check the type of the encoded id to determine if it is a type Location
        const idType = fromGlobalId(value).type
        if (idType === 'RetailerLocation') {
          validateOrdersVars.orderSettings.retailerLocationId = value
          validateOrdersVars.orderSettings.billingAddressId = ''
        } else {
          validateOrdersVars.orderSettings.billingAddressId = value
        }
      }

      if (shippingAddress) {
        // TODO: Prepare the payload to send the locationId
        const { value } = shippingAddress

        // Check the type of the encoded id to determine if it is a type Location
        const idType = fromGlobalId(value).type
        if (idType === 'RetailerLocation') {
          validateOrdersVars.orderSettings.retailerLocationId = value
          validateOrdersVars.orderSettings.shippingAddressId = ''
        } else {
          validateOrdersVars.orderSettings.shippingAddressId = value
        }
      }

      if (paymentMethod) {
        validateOrdersVars.orderSettings.paymentMethodId =
          paymentMethod.value || ''
      }

      if (shippingMethod) {
        validateOrdersVars.orderSettings.shippingMethodId =
          shippingMethod.value || ''
      }

      if (door) {
        // id is encoded as BrandDoor and we need to send as Door
        const brandDoorId = fromGlobalId(door.value)?.id as string
        const doorId = toGlobalId('Door', brandDoorId) as string

        // decode name and code from option label
        const hasCode = door.label.includes('(')
        const doorLabelParts = hasCode ? door.label.split('(') : []
        const doorName = doorLabelParts.length
          ? doorLabelParts[0].trim()
          : door.label
        const doorCode = doorLabelParts.length
          ? doorLabelParts[1].replace(')', '').trim()
          : ''

        validateOrdersVars.orderSettings.door = {
          id: doorId,
          name: doorName,
          code: doorCode,
        }
      }

      validateOrder(validateOrdersVars)
    }
  }, [uploadToS3Response])

  const backHandler = (): void => {
    setCurrentStep(TEMPLATE_SELECTION)
  }

  const retryHandler = (): void => {
    setProgressData({
      progress: 33,
      label: 'Uploading File',
      error: false,
    })
    uploadFile(selectedFiles[0])
  }

  const closeHandler = (): void => {
    setCloseOrderImportModal(true)
  }

  return (
    <Container>
      <ProgressBar
        percent={progressData.progress}
        label={progressData.label}
        error={progressData.error}
        data-testid="upload-progress-bar"
        size={'tiny'}
      />
      {progressData.error && (
        <ButtonsWrapper>
          <OptionButton onClick={backHandler} data-testid="back-button">
            Back
          </OptionButton>
          <OptionButton onClick={retryHandler} data-testid="retry-button">
            Retry
          </OptionButton>
          <OptionButton onClick={closeHandler} data-testid="close-button">
            Close
          </OptionButton>
        </ButtonsWrapper>
      )}
    </Container>
  )
}

export default UploadFileProgressBar
